import { FileType } from '@procureai/constellation/foundation/file';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useFileStorage } from '@procureai/uix';
import { camelCase } from 'change-case';
import { drop, flatten, forEach, isNil, isNotEmpty, lensPath, map, reject, uniqBy, values, view, pipe } from 'ramda';
import { useState } from 'react';
import { DropzoneRootProps, FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Attachment, DropZoneFlags } from '../ConversationInput.types';
import { I18nNamespace } from '~/common/types';
import { useBatchedFileUpload } from '~/hooks/useMultiFileUploadProgress/useMultiFileUploadProgress';

const ACCEPTED_FILE_TYPES: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
};

const sanitize = pipe(values, flatten, map(drop(1)));

const LIST_OF_EXTENSIONS = sanitize(ACCEPTED_FILE_TYPES);
const MAX_SIZE = 100 * 1024 * 1024; // 100 MB
const MAX_FILES = 5;

const fileRejectionErrorLens = lensPath(['errors', 0, 'code']);

export interface UseConversationInputAttachmentsProps {
  enableAttachments: boolean;
}

export const useConversationInputAttachments = ({ enableAttachments }: UseConversationInputAttachmentsProps) => {
  const { t } = useTranslation(I18nNamespace.conversation, { keyPrefix: 'ChatInput.error' });
  const { onError, enqueueErrorSnackbar } = useCustomSnackbar();

  const acceptedFiles = enableAttachments ? ACCEPTED_FILE_TYPES : {};

  const [files, setFiles] = useState<Attachment[]>([]);
  const { upload: falconUpload } = useFileStorage();

  // Using a falcon batch upload over managing individual uploads ourselves
  const upload = async (file: File): Promise<void> => {
    const { error, data } = await falconUpload({ data: { file, variant: FileType.ATTACHMENT } });
    if (error) {
      onError(error);
      // re-throwing the error to ensure proper rejecting in useBatchedFileUpload's uploadBatch
      throw error;
    }
    // Intercepting batch upload here to instantly display files and not once everything completed
    // Therefore no return required and no files handling through useBatchedFileUpload's uploadBatch
    setFiles((prev) => [
      ...prev,
      { fileId: data.uploadId, fileName: file.name, contentType: file.type, size: file.size },
    ]);
  };
  const { uploadBatch, isLoading: isUploading } = useBatchedFileUpload<void>(upload);

  const onDropRejected = (fileRejections: FileRejection[]) => {
    const uniqueByFirstError = uniqBy(view(fileRejectionErrorLens), fileRejections);

    const errorCodes = map<FileRejection, string | undefined>(view(fileRejectionErrorLens), uniqueByFirstError);
    const definedErrorCodes = reject(isNil, errorCodes);

    // Generating 1 toast message for each error code (if present)
    // There are only 4 error codes in FileRejection (3 which might apply)
    // This way, the user has proper guidance on what went wrong in detail
    forEach(
      (code: string) =>
        enqueueErrorSnackbar(
          t(camelCase(code), {
            supportedFileTypes: LIST_OF_EXTENSIONS,
            maxSize: MAX_SIZE,
            maxFiles: MAX_FILES,
          }),
        ),
      definedErrorCodes,
    );
  };

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } = useDropzone({
    accept: acceptedFiles,
    maxSize: MAX_SIZE,
    maxFiles: MAX_FILES,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDrop: uploadBatch,
    onDropRejected,
  });

  const buttonProps = { onClick: open };
  const dropZoneProps: DropZoneFlags & DropzoneRootProps = {
    ...getRootProps(),
    isDragActive,
    isDragAccept,
    isDragReject,
  };

  return {
    attachments: files,
    hasAttachments: isNotEmpty(files),
    isUploading,
    onDelete: (idToRemove: string) => setFiles((prev) => prev.filter((file) => file.fileId !== idToRemove)),
    clearAttachments: () => setFiles([]),
    // prevent props forwarding for drag&drop and upload through button if attachments aren't enabled
    inputProps: enableAttachments ? getInputProps() : undefined,
    buttonProps: enableAttachments ? buttonProps : undefined,
    dropZoneProps: enableAttachments ? dropZoneProps : undefined,
  };
};
